/**
 * The scope picker's "All regions" row (BON-534).
 *
 * Its own file because it is the one row that is not a region: it carries the
 * top-level COUNT rather than a director, and it is the one row a viewer can
 * be refused — the national dashboards endpoints are Admin-only, so a Regional
 * Director is not offered it at all.
 */
import { Link } from 'react-router-dom';

import { SCOPE_ROW_METRICS, type ScopeRowVariant } from './scope-row-metrics';
import type { ScopeHrefFor } from './scope.types';

export interface ScopeAllRegionsRowProps {
  /** How many top-level regions it stands for. */
  topLevelCount: number;
  /** True at all-regions scope. */
  isHere: boolean;
  hrefFor: ScopeHrefFor;
  onPick: () => void;
  variant: ScopeRowVariant;
  /** What the row is called. Defaults to "All regions"; a page can reuse the
   *  row for another whole-scope choice, such as "Your regions". */
  label?: string;
  /** Where it goes. Defaults to the page's all-regions address. */
  to?: string;
}

export function ScopeAllRegionsRow({
  topLevelCount,
  isHere,
  hrefFor,
  onPick,
  variant,
  label = 'All regions',
  to,
}: ScopeAllRegionsRowProps) {
  const size = SCOPE_ROW_METRICS[variant];
  return (
    <Link
      to={to ?? hrefFor(null)}
      role="menuitem"
      {...(isHere ? { 'aria-current': true as const } : {})}
      onClick={onPick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        minHeight: size.rowHeight,
        padding: size.rowPadding,
        marginLeft: size.caretWidth,
        borderRadius: size.radius,
        textDecoration: 'none',
        fontSize: size.label,
        fontWeight: isHere ? 700 : 600,
        color: isHere ? 'var(--nb-primary-2)' : 'var(--nb-ink)',
        background: isHere ? 'var(--nb-primary-soft)' : 'transparent',
      }}
    >
      {label}
      <span style={{ fontSize: size.meta, color: 'var(--nb-subtle)', whiteSpace: 'nowrap' }}>
        {topLevelCount} {topLevelCount === 1 ? 'region' : 'regions'}
      </span>
    </Link>
  );
}
